import React from "react";
import Footer from "./footer";
import Navbar from "./navbar";
import '../index.css';
import { useParams } from 'react-router-dom';
import ItemCrousel from "./home_components/ItemCrousel";
import Loader from "./home_components/loader";
import { useNavigate } from "react-router-dom";
import baseUrl from "../base_url";
import axios from "axios";
import ProductSkeleton from "./components/productSkeleton";
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet';
import toast, { Toaster } from 'react-hot-toast'; // Import Toaster

const ProductGallery = ({ product }) => {
  const [selectedImageIndex, setSelectedImageIndex] = React.useState(0);

  const allImages = React.useMemo(() => {
    const images = [];
    if (product.image && product.image.length > 0) {
      images.push({
        src: product.image[0].src,
        alt: product.image[0].alt || "Product image",
      });

      if(product?.image[0]?.dataAltImage && product?.image[0]?.dataAltImage !== product.image[0]?.src){
        images.push({
            src: product.image[0].dataAltImage,
            alt: product.image[0].alt || "Product alternate image",
        })
      }
      for (let i = 1; i < product.image.length; i++) {
        images.push({
          src: product.image[i].src,
          alt: product.image[i].alt || `Product image ${i + 1}`,
        });
      }
    }
    return images;
  }, [product.image]);

  return (
    <div className="w-full lg:w-3/5 flex flex-col-reverse md:flex-row gap-2">
      {/* Thumbnails */}
      <div className="flex md:flex-col flex-row w-full md:w-20 overflow-x-auto md:overflow-visible relative scrollbar-hide pb-2">
        {allImages.map((img, index) => (
          <div
            key={index}
            className={`min-w-16 w-16 h-16 mb-0 mr-2 md:mr-0 md:mb-2 border cursor-pointer overflow-hidden ${selectedImageIndex === index ? "border-purple-500" : ""}`}
            onClick={() => setSelectedImageIndex(index)}
          >
            <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
          </div>
        ))}
        <div className="hidden md:flex justify-center mt-3">
          {/* <button className="bg-gray-100 border border-gray-200 w-6 h-6 rounded-full flex items-center justify-center cursor-pointer">
            <i className="fa-solid fa-chevron-down"></i>
          </button> */}
        </div>
      </div>

      {/* Main Image */}
      <div className="flex-grow relative flex justify-center items-start">
        <div className="absolute top-2 left-0 bg-gradient-to-r from-purple-700 to-purple-500 text-white px-4 py-1 font-bold z-10 clip-path-sale">
          SALE
        </div>
        <img
          src={allImages[selectedImageIndex]?.src}
          alt={allImages[selectedImageIndex]?.alt}
          className="w-full h-auto object-contain"
        />
      </div>
    </div>
  );
};

const Variant = ({variant}) => {
    return(
        <Link to={`/productpage/${variant.slug}`} className="w-[calc(20%-4px)] min-w-[83px] sm:w-[calc(20%-4px)] border-[1px] hover:border-purple-500 transition-colors">
            <img className="w-full h-full object-cover aspect-square" src={variant.image[0].src || variant.image[1]} alt={`Variant: ${variant.title || 'Product variant'}`} />
        </Link>
    )
}

const Description = ({desc}) => {
    return(
        <p className="pb-[5px]">
            <span className="font-bold">•</span> {desc}
        </p>
    )
}

const generateProductSchema = (product) => {
    const priceValidUntil = new Date();
    priceValidUntil.setDate(priceValidUntil.getDate() + 30);
    const priceValidUntilISO = priceValidUntil.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  
    const schema = {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": product.title,
      "image": product.image && product.image.length > 0 ? product.image.map(img => img.src) : [],
      "description": product.description ? product.description.split("#$#").join(" ") : "",
      "sku": product.articleCode,
      "brand": {
        "@type": "Brand",
        "name": product.brandName || "Jutexx"
      },
      "offers": {
        "@type": "Offer",
        "price": product.numericPrice,
        "priceCurrency": "INR",
        "availability": product.inventory > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "url": `https://jutexx.com/productpage/${product.slug}`,
        "priceValidUntil": priceValidUntilISO,
        "priceSpecification": {
          "@type": "PriceSpecification",
          "price": product.numericPrice,
          "priceCurrency": "INR"
        }
      },
      "category": product.category,
      "mpn": product.articleCode,
      "identifier": product._id
    };
  
    // Only add aggregateRating if there are actual reviews
    if (product.reviews && product.reviews.length > 0) {
      let averageRating = 5;
      if (product.reviews.some(review => review.rating)) {
        const ratingsSum = product.reviews.reduce((sum, review) => sum + (review.rating || 5), 0);
        averageRating = (ratingsSum / product.reviews.length).toFixed(1);
      }
  
      schema.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": averageRating.toString(),
        "reviewCount": product.reviews.length,
        "bestRating": "5",
        "worstRating": "1"
      };
    }
  
    // Add variants if they exist
    if (product.variants && product.variants.length > 0) {
      schema.hasVariant = product.variants.map(variant => ({
        "@type": "ProductModel",
        "name": variant.title,
        "image": variant.image && variant.image.length > 0 ? variant.image[0].src : "",
        "url": `https://jutexx.com/productpage/${variant.slug}`
      }));
    }
  
    return JSON.stringify(schema);
};

// Custom hook for login status
const useAuth = () => {
    const [loggedIn, setLoggedIn] = React.useState(false);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        const checkLoginStatus = async () => {
            try {
                const accessToken = localStorage.getItem("accessToken");
                if (!accessToken) {
                    setLoading(false);
                    return;
                }

                const response = await axios.get(`${baseUrl}api/auth/IsLoggedIn`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    withCredentials: true,
                });
                setLoggedIn(true);
            } catch (err) {
                console.error("Error while checking login status:", err.response?.data || err.message);
                setLoggedIn(false);
            } finally {
                setLoading(false);
            }
        };

        checkLoginStatus();
    }, []);

    return { loggedIn, loading };
};

// Custom hook for product data
const useProduct = (productSlug) => {
    const [product, setProduct] = React.useState(null);
    const [productForCrousel, setProductForCrousel] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        const fetchProduct = async () => {
            if (!productSlug) return;
            
            setLoading(true);
            setError(null);
            
            try {
                const [productResponse, crouselResponse] = await Promise.all([
                    fetch(`${baseUrl}api/products/data/item/${productSlug}`),
                    fetch(`${baseUrl}api/products/data/Jutexx`)
                ]);

                if (!productResponse.ok) {
                    throw new Error('Product not found');
                }
                if (!crouselResponse.ok) {
                    throw new Error('Failed to load related products');
                }

                const productData = await productResponse.json();
                const crouselData = await crouselResponse.json();
                
                setProduct(productData.data);
                setProductForCrousel(crouselData.data);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchProduct();
    }, [productSlug]);

    return { product, productForCrousel, loading, error };
};

function ProductPage2() {
    const navigate = useNavigate();
    const { num } = useParams();
    
    // Custom hooks
    const { loggedIn } = useAuth();
    const { product, productForCrousel, loading, error } = useProduct(num);

    // State
    const [quantity, setQuantity] = React.useState(1);
    const [added, setAdded] = React.useState(false);
    const [cartLoading, setCartLoading] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");
    const [size, setSize] = React.useState('largest');

    // Initialize selection when product loads
    const [selected, setSelected] = React.useState({
        code: '',
        color: '',
        size: "M"
    });

    React.useEffect(() => {
        if (product) {
            setSelected({
                code: product?.articleCode,
                color: product?.swatches?.[0]?.colorName || '',
                size: "M"
            });
        }
    }, [product]);

    // Handle responsive sizing
    React.useEffect(() => {
        const handleResize = () => {
            setSize(window.innerWidth < 768 ? 'smaller' : 'largest');
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Update document title
    React.useEffect(() => {
        document.title = `Jutexx | ${product?.title || "Shopping"}`;
    }, [product]);

    // Add to cart function with improved error handling
    const addToCart = async () => {
        if (cartLoading) return; // Prevent double-clicks
        
        setCartLoading(true);
        setErrorMessage("");

        // Check login status first and show toast with delayed redirect
        if (!loggedIn) {
            toast.error('Please login first to add items to cart', {
                duration: 3000,
            });
            
            // Delay redirect by 1 second to show the toast
            setTimeout(() => {
                navigate("/login");
            }, 1000);
            
            setCartLoading(false);
            return;
        }

        try {
            const accessToken = localStorage.getItem("accessToken");
            
            if (!accessToken) {
                toast.error('Session expired. Please login again.', {
                    duration: 3000,
                });
                
                setTimeout(() => {
                    navigate("/login");
                }, 1000);
                
                setCartLoading(false);
                return;
            }

            const response = await axios.post(
                `${baseUrl}api/cart/add-to-cart/${product._id}`,
                { quantity: quantity },
                {
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json', 
                    },
                    timeout: 10000, // 10 second timeout
                }
            );

            // Enhanced success toast with action
            toast.success(
                (t) => (
                    <div className="flex items-center justify-between w-full">
                        <span className="mr-3">
                            {quantity > 1 ? `${quantity}x ${product.title}` : product.title} added to cart!
                        </span>
                        <button
                            onClick={() => {
                                navigate('/cart');
                                toast.dismiss(t.id);
                            }}
                            className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 ml-2"
                        >
                            View Cart
                        </button>
                    </div>
                ),
                { 
                    duration: 4000,
                    style: {
                        maxWidth: '500px',
                    }
                }
            );
            
            setAdded(true);
        } catch (error) {
            console.error("Error while adding to cart", error);
            
            let errorMsg = "Failed to add item to cart. Please try again.";
            
            if (error.code === 'ECONNABORTED') {
                errorMsg = "Request timed out. Please check your connection and try again.";
            } else if (error.response?.status === 401) {
                errorMsg = "Session expired. Please login again.";
                setTimeout(() => {
                    navigate("/login");
                }, 1000);
            } else if (error.response?.status === 400) {
                errorMsg = error.response?.data?.message || "Invalid request. Please try again.";
            } else if (error.response?.status === 409) {
                errorMsg = "Item already in cart or out of stock.";
            }
            
            toast.error(errorMsg, {
                duration: 4000,
            });
            setErrorMessage(errorMsg);
        } finally {
            setCartLoading(false);
        }
    };

    // Loading state
    if (loading) return <ProductSkeleton />;

    // Error state
    if (error) return (
        <>
            <Navbar />
            <div className="min-h-[80vh] flex flex-col justify-center items-center text-red-700 p-4">
                <div className="text-center bg-white border border-red-200 rounded-2xl shadow-md p-8 max-w-md w-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 5a7 7 0 00-7 7v0a7 7 0 0014 0v0a7 7 0 00-7-7z" />
                    </svg>
                    <h1 className="text-2xl font-semibold mb-2">Oops! Something went wrong.</h1>
                    <p className="text-sm text-gray-600 mb-4">
                        {error.message === 'Product not found' 
                            ? "The product you're looking for doesn't exist." 
                            : "Couldn't load the product due to a network problem."
                        }
                    </p>
                    <button 
                        onClick={() => window.location.reload()} 
                        className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
            <Footer />
        </>
    );

    // Product not found
    if (!product) return (
        <>
            <Navbar />
            <div className="min-h-[80vh] flex flex-col justify-center items-center p-4">
                <h1 className="text-2xl font-semibold mb-2">Product not found</h1>
                <Link to="/" className="text-blue-600 hover:underline">Return to homepage</Link>
            </div>
            <Footer />
        </>
    );

    return (
        <div className="font-sans text-gray-800 flex flex-col">
            {/* Add Toaster component for toast notifications */}
            <Toaster 
                position="top-center"
                reverseOrder={false}
                gutter={8}
                containerClassName=""
                containerStyle={{}}
                toastOptions={{
                    // Define default options
                    className: '',
                    duration: 4000,
                    style: {
                        background: '#fff',
                        color: '#363636',
                        fontSize: '14px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        padding: '12px 16px',
                    },
                    // Default options for specific types
                    success: {
                        duration: 4000,
                        iconTheme: {
                            primary: '#10B981',
                            secondary: '#FFFFFF',
                        },
                    },
                    error: {
                        duration: 4000,
                        iconTheme: {
                            primary: '#EF4444',
                            secondary: '#FFFFFF',
                        },
                    },
                }}
            />
            
            <Navbar />
            <Helmet>
                <title>{product.title} | Eco-Friendly Jute Products</title>
                <meta name="description" content={`Buy high-quality ${product.title} made from eco-friendly jute. Perfect for sustainable living.`} />
                <meta name="keywords" content="jute, eco-friendly, jute baskets, jute bags, handmade, organic, sustainable products" />
                
                {/* Open Graph */}
                <meta property="og:title" content={product.title} />
                <meta property="og:description" content={`Eco-friendly ${product.title} made from high-quality jute.`} />
                <meta property="og:image" content={product.image && product.image.length > 0 ? product.image[0].src : ''} />
                <meta property="og:url" content={`https://jutexx.com/productpage/${product.slug}`} />
                <meta property="og:type" content="product" />
                
                {/* Twitter Card */}
                <meta name="twitter:title" content={product.title} />
                <meta name="twitter:description" content={`Eco-friendly ${product.title} for sustainable living.`} />
                <meta name="twitter:image" content={product.image && product.image.length > 0 ? product.image[0].src : ''} />
                <meta name="twitter:card" content="summary_large_image" />
                
                {/* JSON-LD Structured Data */}
                <script type="application/ld+json">
                    {generateProductSchema(product)}
                </script>
            </Helmet>
            
            <div className="max-w-[100vw] md:max-w-7xl mx-auto px-4 sm:px-6 flex flex-col lg:flex-row gap-8 my-5">
                <ProductGallery product={product} />

                {/* Product Details */}
                <div className="bg-w-full lg:w-2/5 flex flex-col">
                    <h1 className="text-xl sm:text-2xl mb-2 text-gray-800 font-medium">{product.title}</h1>
                    
                    <div className="flex items-center gap-3 mb-1">
                        <span className="text-2xl sm:text-3xl font-bold text-gray-800">{product.price}</span>
                    </div>
                    
                    <p className="text-xs text-gray-500 mb-4">Inclusive of all Taxes.</p>

                    <div className="flex items-center gap-4 mb-5">
                        <span className="bg-green-50 text-green-700 px-3 py-1 rounded text-sm font-medium">
                            Free Delivery
                        </span>
                    </div>

                    {/* Variants Section */}
                    {product?.variants?.length > 0 && (
                        <div className="pb-[20px]">
                            <h2 className="font-semibold mb-2">Variants</h2>
                            <div className="flex flex-row gap-1 flex-wrap">
                                {product.variants.map((v, index) => (
                                    <Variant key={v.slug || index} variant={v} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Product Options */}
                    <div className="mb-5">
                        <h3 className="text-base mb-2 font-medium">Quantity</h3>
                        <div className="flex gap-2">
                            <div className="flex-1">
                                <select 
                                    value={quantity} 
                                    onChange={(e) => setQuantity(Number(e.target.value))}
                                    className="w-full p-3 border border-gray-300 rounded text-base focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                >
                                    {Array.from({ length: Math.min(product.inventory, 5) }, (_, index) => (
                                        <option key={index} value={index + 1}>{index + 1}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Add to Cart Section */}
                    <div className="flex flex-col sm:flex-row gap-2 mb-6">
                        {product.inventory > 0 ? (
                            !added ? (
                                <div className="flex flex-col w-full gap-[10px]">
                                    {product.inventory < 10 && (
                                        <p className="text-sm sm:text-base">
                                            Only <span className="text-red-600 font-bold">{product.inventory} Left in Stock!</span> Order Now!
                                        </p>
                                    )}

                                    <button 
                                        className="flex-1 py-3 sm:py-4 border border-black bg-white text-black font-bold flex justify-center items-center hover:bg-gray-100 min-w-[120px] relative transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={addToCart}
                                        disabled={cartLoading}
                                        style={{
                                            animation: "glowPulse 2s infinite",
                                            boxShadow: "0 0 10px rgba(72, 187, 120, 0.5)"
                                        }}
                                    >
                                        <style jsx>{`
                                            @keyframes glowPulse {
                                                0% { box-shadow: 0 0 10px rgba(72, 187, 120, 0.2); }
                                                50% { box-shadow: 0 0 15px rgba(72, 187, 120, 0.8); }
                                                100% { box-shadow: 0 0 10px rgba(72, 187, 120, 0.2); }
                                            }
                                        `}</style>
                                        <span className={`${cartLoading ? 'opacity-0' : 'opacity-100'}`}>
                                            ADD TO BAG
                                        </span>
                                        {cartLoading && (
                                            <span className="absolute p-[2px] inset-0 flex items-center justify-center  overflow-hidden">
                                                <Loader />
                                            </span>
                                        )}
                                    </button>
                                    
                                    {errorMessage && (
                                        <div className="text-red-500 text-sm mt-2 p-2 bg-red-50 border border-red-200 rounded">
                                            {errorMessage}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Link 
                                    to="/cart"
                                    className="py-3 sm:py-4 border border-green-500 bg-green-50 text-green-700 font-bold flex justify-center items-center hover:bg-green-100 text-center w-full transition-colors"
                                >
                                    GO TO CART
                                </Link>
                            )
                        ) : (
                            <span className="py-3 sm:py-4 border border-gray-500 bg-gray-200 text-gray-700 font-bold flex justify-center items-center text-center w-full">
                                OUT OF STOCK
                            </span>
                        )}
                    </div>

                    {/* Delivery Features */}
                    <div className="border-t border-b border-gray-200 py-4 mb-5">
                        <div className="space-y-4">
                            <div className="flex items-center gap-4">
                                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-800">
                                    <i className="fa-solid fa-truck-fast"></i>
                                </div>
                                <div className="text-sm">Get it delivered in 3-7 days</div>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-800">
                                    <i className="fa-solid fa-money-bill"></i>
                                </div>
                                <div className="text-sm">Cash on Delivery</div>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-800">
                                    <i className="fa-solid fa-rotate-left"></i>
                                </div>
                                <div className="text-sm">Return and exchange within 5 days</div>
                            </div>
                        </div>
                    </div>

                    {/* Product Description */}
                    <div className="py-4 border-b border-gray-200 mb-5">
                        <h3 className="text-lg mb-2 font-medium">Product Description</h3>
                        <div className="text-sm sm:text-base">
                            {product?.description && 
                                product.description.split("#$#").map((desc, index) => (
                                    <Description key={index} desc={desc} /> 
                                ))
                            }
                        </div>
                    </div>

                    {/* Product Information */}
                    <div className="py-4 border-b border-gray-200 mb-5">
                        <h3 className="text-lg mb-2 font-medium">Product Information</h3>
                        <div className="grid mb-2 gap-2">
                            <div className="grid grid-cols-2">
                                <span className="text-gray-500">Design</span>
                                <span className="font-medium">Artisanal</span>
                            </div>
                            <div className="grid grid-cols-2">
                                <span className="text-gray-500">Type</span>
                                <span className="font-medium">Handicraft</span>
                            </div>
                        </div>

                        {/* Trust Badges */}
                        <div className="flex justify-center gap-4 sm:gap-8 mt-8 sm:mt-12">
                            <div className="flex flex-col justify-center items-center">
                                <img 
                                    src="https://cdn-image.blitzshopdeck.in/ShopdeckWebsiteAssets/tr:w-600,f-webp,fo-auto/product_quality_trustmarkers/SUPERIOR_QUALTIY_TRUSTBADGE.webp" 
                                    alt="Superior Quality" 
                                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-full"
                                />
                                <p className="text-xs font-bold text-center">Superior Quality</p>
                            </div>
                            <div className="flex flex-col justify-center items-center">
                                <img 
                                    src="https://cdn-image.blitzshopdeck.in/ShopdeckWebsiteAssets/tr:w-600,f-webp,fo-auto/product_quality_trustmarkers/NEXT_DAY_DISPATCH.webp" 
                                    alt="Next Day Dispatch" 
                                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-full"
                                />
                                <p className="text-xs font-bold text-center">Fast Dispatch</p>
                            </div>
                            <div className="flex flex-col justify-center items-center">
                                <img 
                                    src="https://cdn-image.blitzshopdeck.in/ShopdeckWebsiteAssets/tr:w-600,f-webp,fo-auto/product_quality_trustmarkers/FREE_SHIPPING.webp" 
                                    alt="Free Shipping" 
                                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-full"
                                />
                                <p className="text-xs font-bold text-center">FREE SHIPPING</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Carousel Section */}
            {productForCrousel && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full pb-20">
                    <div className="overflow-hidden">
                        <ItemCrousel
                            data={productForCrousel}
                            label={"Others also bought"}
                            size={size}
                        />
                    </div>
                </div>
            )}

            <Footer />
            
            {/* Custom Styles */}
            <style jsx>{`
                .clip-path-sale {
                    clip-path: polygon(0 0, 100% 0, 100% 100%, 15% 100%);
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;  /* IE and Edge */
                    scrollbar-width: none;  /* Firefox */
                }
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;  /* Chrome, Safari and Opera */
                }
            `}</style>
        </div>
    );
}

export default ProductPage2;